import * as React from 'react';
import { useState } from 'react';
import { SignupStep2View, SignupStep2ViewProps } from './SignupStep2.view';
import { useForm, RegisterOptions } from 'react-hook-form';
import { FormOptions, ValidationPatterns } from '../../../constants/form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { RegisterMutationFn, RegisterMutationVariables, TimezoneSetting } from '../../../models/graphql';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { useReactiveVar } from '@apollo/client';
import { IInvitation } from '../../../models/auth';
import { convertTimezoneNameToEnum } from '../../../helpers/timeFormatter';
import { TimezoneNames } from '../../../constants/timezones';
import { InputTypes } from '../../../constants/form';
import { useDidMount } from 'hooks/useDidMount';
import { UseFormProps } from 'react-hook-form/dist/types/form';
import { storeAuthToken } from '../../../hooks/useAuth';
import { changeSelectedWorkspaceId, invitationDetailsVar, socialOAuthVar } from '../../../providers/AppProvider';

export type SignupStep2ControllerProps = Omit<
  SignupStep2ViewProps,
  | 'emailRules'
  | 'tosRules'
  | 'nameRules'
  | 'passwordRules'
  | 'timezoneRules'
  | 'socialAuthTokenRules'
  | 'formInstance'
  | 'onSubmit'
  | 'timezone'
  | 'passwordButton'
  | 'passwordShowToggle'
> & {
  email: string;
  registerMutation: RegisterMutationFn;
};

export type RegisterWithEmailFormVariables = {
  name: string;
  tos: string;
  email: string;
  password: string;
  timezone: TimezoneSetting;
  socialAuthToken: string;
  invitationToken: string;
};

export const SignupStep2Controller = (props: SignupStep2ControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const invitation: IInvitation | null = useReactiveVar(invitationDetailsVar);
  const defaultFormValues = {
    email: props.email || props.signupData?.email || '',
    tos: '',
    name: props.signupData?.firstName || '',
    timezone: convertTimezoneNameToEnum(Intl.DateTimeFormat().resolvedOptions().timeZone as TimezoneNames),
    socialAuthToken: props.signupData?.clientToken || ''
  } as RegisterWithEmailFormVariables;

  const [passwordButton, setPasswordButton] = useState<SquareButtonIconsEnum.show | SquareButtonIconsEnum.hide>(
    SquareButtonIconsEnum.show
  );
  const [passwordFiledType, setPasswordFiledType] = useState<InputTypes.text | InputTypes.password | InputTypes.hidden>(
    props.passwordFiledType
  );

  const formOptions = { ...FormOptions } as UseFormProps<RegisterWithEmailFormVariables>;
  const formInstance = useForm<RegisterWithEmailFormVariables>({
    ...formOptions,
    defaultValues: defaultFormValues
  });
  const {
    formState: { touchedFields },
    getValues
  } = formInstance;
  const emailRules: RegisterOptions = {
    required: ValidationMessages.email.required,
    pattern: {
      value: ValidationPatterns.email,
      message: ValidationMessages.email.invalid
    }
  };
  const tosRules: RegisterOptions = {
    required: ValidationMessages.tos.required
  };
  const nameRules: RegisterOptions = {
    required: ValidationMessages.name.required,
    pattern: {
      value: ValidationPatterns.name,
      message: ValidationMessages.name.invalid
    }
  };
  const passwordRules: RegisterOptions =
    props.passwordFiledType !== InputTypes.hidden
      ? {
          required: {
            message: ValidationMessages.password.required,
            value: !!touchedFields.password && getValues('password') === ''
          },
          pattern: {
            value: ValidationPatterns.password,
            message: ValidationMessages.password.invalid
          }
        }
      : {
          required: false
        };
  const timezoneRules: RegisterOptions = {
    required: ValidationMessages.timezone.required
  };
  const socialAuthTokenRules: RegisterOptions = {
    required: false
  };

  // show hide password on input filed
  const passwordShowToggle = () => {
    setPasswordButton(prevState =>
      prevState === SquareButtonIconsEnum.show ? SquareButtonIconsEnum.hide : SquareButtonIconsEnum.show
    );
    setPasswordFiledType(prevState => (prevState === InputTypes.password ? InputTypes.text : InputTypes.password));
  };

  const onSubmit = async (data: RegisterWithEmailFormVariables) => {
    const dataCopy: RegisterMutationVariables = {
      input: {
        email: data.email,
        firstName: data.name,
        password: data.password,
        timezone: data.timezone,
        socialAuthToken: data.socialAuthToken
      }
    };
    // remove non mandatory values if they are not present in the form submission,
    // since social and email register can have different data to be passed to register
    if (!dataCopy.input.socialAuthToken) {
      delete dataCopy.input.socialAuthToken;
    }
    if (!dataCopy.input.timezone) {
      delete dataCopy.input.timezone;
    }
    if (!dataCopy.input.password) {
      delete dataCopy.input.password;
    }
    if (invitation) {
      dataCopy.input.invitationToken = invitation.token;
    }
    try {
      const result = await props.registerMutation({ variables: dataCopy });
      if (result.data?.register.token) {
        storeAuthToken(result.data?.register.token);
        if (invitation) {
          invitationDetailsVar(null);
        }
        changeSelectedWorkspaceId(result.data.register.user.organizations[0]?.workspaces[0]?.id || '', '/');
      }
    } catch (error) {
      // we need to have catch since a runtime error will be thrown due to unhandled error from promise
    }
  };

  useDidMount(() => {
    // we want to be sure that socialOAuthVar hes been unset on unmount, if we click on go back for instance
    return () => {
      socialOAuthVar(null);
    };
  });

  const viewProps: SignupStep2ViewProps = {
    ...props,
    emailRules,
    tosRules,
    nameRules,
    passwordRules,
    timezoneRules,
    socialAuthTokenRules,
    formInstance,
    onSubmit,
    passwordButton,
    passwordShowToggle,
    passwordFiledType
  };
  return <SignupStep2View {...viewProps} />;
};
